import React, {useState} from "react";
import {publicCustomerService} from "../../services/publicCustomerService";
import notificationService from "../../services/NotificationService";
import {getBusinessOrCustomerName} from "../../shared/sharedFunctions";

interface PublicContactInfoCardProps {
    heading: string;
    mapBase64: string;
    onEnlarge: () => void;
    webIdUniqueToken: string;
    customerData: any;
    setCustomerData: React.Dispatch<React.SetStateAction<any>>;
    // Optional read-only rows shown after the property (e.g. the scheduled service date).
    extraRows?: React.ReactNode;
}

const PublicContactInfoCard: React.FC<PublicContactInfoCardProps> = ({
    heading, mapBase64, onEnlarge, webIdUniqueToken, customerData, setCustomerData, extraRows
}) => {
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [draft, setDraft] = useState({firstName: '', lastName: '', businessName: '', homePhone: '', email: ''});

    const isCommercial = !!customerData.isCommercialAddress;

    const startEdit = () => {
        setDraft({
            firstName: customerData.firstName ?? '',
            lastName: customerData.lastName ?? '',
            businessName: customerData.businessName ?? '',
            homePhone: customerData.homePhone ?? '',
            email: customerData.email ?? '',
        });
        setEditing(true);
    };

    const setField = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setDraft(prev => ({...prev, [field]: e.target.value}));

    const save = async () => {
        setSaving(true);
        const payload = {
            firstName: draft.firstName.trim(),
            lastName: draft.lastName.trim(),
            // Business name is only editable for commercial addresses; preserve the existing value otherwise.
            businessName: isCommercial ? draft.businessName.trim() : (customerData.businessName ?? ''),
            homePhone: draft.homePhone.trim(),
            email: draft.email.trim(),
        };
        const r = await publicCustomerService.updateProfile(webIdUniqueToken, payload);
        setSaving(false);
        if (!r) return;
        setCustomerData(prev => ({...prev, ...payload}));
        setEditing(false);
        notificationService.success("Your information has been updated.");
    };

    const mapSrc = mapBase64 && mapBase64 !== '' ? `data:image/png;base64,${mapBase64}` : '';

    return (
        <div className="card p-4 mb-3">
            <div className="d-flex justify-content-between align-items-start mb-4">
                <h3 className="mb-0 fw-bold"><i className="far fa-map-location-dot text-icon me-1"></i> {heading}</h3>
                {!editing && (
                    <a role="button" onClick={startEdit}><i className="far fa-pen me-1"></i> edit info</a>
                )}
            </div>
            <div className="row gx-5">
                <div className="col-md-auto mb-3 mb-md-0 text-end">
                    <div className="map-thumbnail mb-2">
                        <img alt='map' src={mapSrc}/>
                    </div>
                    <a onClick={onEnlarge} role="button" data-bs-toggle="modal" data-bs-target="#map-modal">enlarge map <i
                        className="far fa-external-link"></i></a>
                </div>
                <div className="col-md">
                    {!editing ? (
                        <>
                            <div className="mb-2">
                                <h3 className="mb-0">{getBusinessOrCustomerName(customerData)}</h3>
                                <label className="light-label">contact name</label>
                            </div>
                            <div className="mb-2">
                                <h3 className="mb-0">{customerData.standardizedAddress}</h3>
                                <label className="light-label">property</label>
                            </div>
                            {extraRows}
                            <div className="mb-2">
                                <h3 className="mb-0">{customerData.homePhone}</h3>
                                <label className="light-label">phone</label>
                            </div>
                            <div>
                                <h3 className="mb-0">{customerData.email}</h3>
                                <label className="light-label">email</label>
                            </div>
                        </>
                    ) : (
                        <>
                            {isCommercial && (
                                <div className="mb-2">
                                    <label className="light-label">business name</label>
                                    <input className="form-control" value={draft.businessName}
                                           onChange={setField('businessName')} maxLength={100}/>
                                </div>
                            )}
                            <div className="row">
                                <div className="col-md-6 mb-2">
                                    <label className="light-label">first name</label>
                                    <input className="form-control" value={draft.firstName}
                                           onChange={setField('firstName')} maxLength={50}/>
                                </div>
                                <div className="col-md-6 mb-2">
                                    <label className="light-label">last name</label>
                                    <input className="form-control" value={draft.lastName}
                                           onChange={setField('lastName')} maxLength={70}/>
                                </div>
                            </div>
                            <div className="mb-2">
                                <h3 className="mb-0">{customerData.standardizedAddress}</h3>
                                <label className="light-label">property (call the office to change)</label>
                            </div>
                            {extraRows}
                            <div className="mb-2">
                                <label className="light-label">phone</label>
                                <input className="form-control" value={draft.homePhone}
                                       onChange={setField('homePhone')} maxLength={20}/>
                            </div>
                            <div className="mb-3">
                                <label className="light-label">email</label>
                                <input className="form-control" type="email" value={draft.email}
                                       onChange={setField('email')} maxLength={256}/>
                            </div>
                            <div className="d-flex gap-2">
                                <button type="button" className="btn btn-primary" disabled={saving} onClick={save}>
                                    <i className="far fa-check"></i> Save
                                </button>
                                <button type="button" className="btn btn-outline-secondary" disabled={saving}
                                        onClick={() => setEditing(false)}>
                                    Cancel
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PublicContactInfoCard;
